import { ThemeProvider, Theme } from "@emotion/react";
import { createTheme, Theme as MuiTheme, ThemeProvider as MuiThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useErrorBoundary } from "react-error-boundary";
import { RootState } from "../store";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { getDesignSettings } from "../../features/settings/api/designSettings.service";
import { handleErrorWithBoundary } from "@shared/lib/errors"
import { StyledApp } from "../../App.styled";

declare module "@emotion/react" {
  export interface Theme {
    colors: {
      main_color: string;
      input_text_color: string;
      input_title_text_color: string;
      input_border_color: string;
      header_color: string;
      background_color: string;
      section_color: string;
      highlight_color: string;
    };
  }
}

const DEFAULT_THEME_COLORS: Theme["colors"] = {
  main_color: "rgb(38, 38, 38)",
  input_text_color: "rgb(255, 255, 255)",
  input_title_text_color: "rgb(155, 155, 155)",
  input_border_color: "rgba(0, 0, 0, 0.3)",
  header_color: "rgb(38, 38, 38)",
  background_color: "rgb(255, 255, 255)",
  section_color: "rgb(255, 255, 255)",
  highlight_color: "rgb(64, 122, 214)",
};

interface AppThemeProviderProps {
  children: React.ReactNode;
}

export default function AppThemeProvider({ children }: AppThemeProviderProps) {
  const designSettings = useSelector((state: RootState) => state.settings.designSettings);
  const { showBoundary } = useErrorBoundary();
  const { user } = useAuth();

  const [themeColors, setThemeColors] = useState<Theme>();
  const [muiTheme, setMuiTheme] = useState<MuiTheme>();
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const fetchUserThemeColors = async () => {
      try {
        if (user) {
          if (designSettings) {
            setThemeColors({ colors: designSettings });
          } else {
            abortControllerRef.current?.abort();
            abortControllerRef.current = new AbortController();

            const data = await getDesignSettings(
              { sort: ["id"], filter: { user_id: user.id } },
              abortControllerRef.current.signal,
            );

            if (data.length > 0) {
              setThemeColors({ colors: data[0] });
            }
          }
        } else {
          setThemeColors({ colors: DEFAULT_THEME_COLORS });
        }
      } catch (error) {
        handleErrorWithBoundary(error, showBoundary);
      }
    };

    fetchUserThemeColors();
  }, [user, designSettings, showBoundary]);

  useEffect(() => {
    setMuiTheme(
      createTheme({
        palette: {
          primary: {
            main: themeColors?.colors.highlight_color || "rgb(64, 122, 214)",
          },
          background: {},
        },
        typography: {
          h1: {
            fontSize: "3rem",
            fontWeight: 600,
          },
          h2: {
            fontSize: "1.75rem",
            fontWeight: 600,
          },
          h3: {
            fontSize: "1.5rem",
            fontWeight: 600,
          },
        },
      }),
    );
  }, [themeColors]);

  if (!themeColors || !muiTheme) {
    return null;
  }

  return (
    <ThemeProvider theme={themeColors}>
      <MuiThemeProvider theme={muiTheme}>
        <StyledApp>
          <LocalizationProvider dateAdapter={AdapterDayjs}>{children}</LocalizationProvider>
        </StyledApp>
      </MuiThemeProvider>
    </ThemeProvider>
  );
}
